//* Import necessary modules
import { useMutation, useQueryClient } from "@tanstack/react-query";

//! Import API
import { permissionAPI } from "../permissions/permissionAPI";

//? Import types
import type {
  AddPermissionsRequest,
  AddPermissionsResponse,
} from "../permissions/types/types";

/**
 * Custom hook `usePermissions` to manage adding/updating user permissions.
 *
 * @param {string} token - Authentication token for API requests.
 * @param {string} userId - Target user ID.
 * @returns {Object} The hook returns functions and states related to the add permissions mutation.
 */
export const usePermissions = (token: string, userId: string) => {
  const queryClient = useQueryClient();

  // * Mutation function to add permissions
  const mutationFn = async (
    permissionsData: AddPermissionsRequest
  ): Promise<AddPermissionsResponse> => {
    return permissionAPI.addPermissions(userId, permissionsData, token);
  };

  // * Using `useMutation` with correct types
  const addPermissionsMutation = useMutation<
    AddPermissionsResponse,
    Error,
    AddPermissionsRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userPermissions", userId] });
    },
    onError: (error: any) => {
      // ! Error handling for add permissions mutation
      console.error("Error in add permissions mutation:", error);
    },
  });

  return {
    addPermissions: addPermissionsMutation.mutate,
    isAdding: addPermissionsMutation.isPending,
    addError: addPermissionsMutation.isError
      ? addPermissionsMutation.error
      : null,
    addSuccess: addPermissionsMutation.isSuccess,
  };
};
